import React, { useState, useEffect } from 'react';
import { Search, Filter, RefreshCw, AlertCircle, Megaphone } from 'lucide-react';
import { getAllPromotions, getPromotionsStats } from '../../../services/adminApi';
import { PageSkeleton } from '../DashboardLoader';

const AdminPromotionInsights = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    paused: 0,
    expired: 0,
    total_impressions: 0,
    total_clicks: 0,
    total_conversions: 0,
    total_revenue: 0,
    average_ctr: 0,
    average_conversion_rate: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPromotions();
  }, [filterStatus]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les promotions et les statistiques en parallèle
      const [promotionsData, statsData] = await Promise.all([
        getAllPromotions(filterStatus === 'all' ? null : filterStatus),
        getPromotionsStats()
      ]);

      setPromotions(promotionsData || []);
      setStats(statsData || {
        total: 0,
        active: 0,
        paused: 0,
        expired: 0,
        total_impressions: 0,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: 0,
        average_ctr: 0,
        average_conversion_rate: 0
      });

    } catch (err) {
      console.error('Erreur lors du chargement des promotions:', err);
      setError('Erreur lors du chargement des promotions');
      // En cas d'erreur, initialiser avec des valeurs par défaut
      setPromotions([]);
      setStats({
        total: 0,
        active: 0,
        paused: 0,
        expired: 0,
        total_impressions: 0,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: 0,
        average_ctr: 0,
        average_conversion_rate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredPromotions = promotions.filter(promotion => {
    const matchesSearch = promotion.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promotion.restaurant_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || promotion.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-gray-100 text-gray-800',
      draft: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      active: 'Active',
      paused: 'En pause',
      expired: 'Expirée',
      draft: 'Brouillon',
      cancelled: 'Annulée'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getFormatLabel = (format) => {
    if (format == null) return '—';
    const labels = { banner: 'Bande', floating: 'Flottante', section: 'Section' };
    return labels[format] || String(format);
  };

  // Cartes stats : uniquement les données renvoyées par l’API (pas d’icônes colorées)
  const statsCards = [
    { title: 'Total Promotions', value: stats.total },
    { title: 'Actives', value: stats.active },
    { title: 'En pause', value: stats.paused },
    { title: 'Expirées', value: stats.expired },
    { title: 'Impressions', value: Number(stats.total_impressions || 0).toLocaleString() },
    { title: 'Clics', value: Number(stats.total_clicks || 0).toLocaleString() },
    { title: 'Conversions', value: stats.total_conversions ?? 0 },
    { title: 'Revenu total (€)', value: `${Number(stats.total_revenue || 0).toLocaleString()}€` },
    { title: 'CTR moyen (%)', value: `${Number(stats.average_ctr || 0)}%` },
    { title: 'Taux conversion (%)', value: `${Number(stats.average_conversion_rate || 0)}%` }
  ];

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Insights Promotions</h1>
          <p className="text-gray-600">Analyse des promotions créées par les restaurateurs sur la plateforme</p>
        </div>
        <button
          onClick={fetchPromotions}
          className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
        {statsCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg p-4">
            <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
            <p className="text-xl font-bold text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par titre ou restaurant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="paused">En pause</option>
              <option value="expired">Expirées</option>
              <option value="draft">Brouillons</option>
            </select>
          </div>
        </div>
      </div>

      {/* Promotions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">
                  Promotion
                </th>
                <th className="px-4 py-3">
                  Restaurant
                </th>
                <th className="px-4 py-3">
                  Statut
                </th>
                <th className="px-4 py-3">
                  Format
                </th>
                <th className="px-4 py-3">
                  Performance
                </th>
                <th className="px-4 py-3">
                  Revenu
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPromotions.map((promotion) => (
                <tr key={promotion.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{promotion.title ?? '—'}</div>
                      <div className="text-xs text-gray-500">
                        {promotion.discount_percentage != null ? `${promotion.discount_percentage}% réduction` : '—'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {promotion.restaurant_name ?? '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(promotion.status)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getFormatLabel(promotion.display_format)}</div>
                    <div className="text-xs text-gray-500 capitalize">{promotion.media_type ?? '—'}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    <div>Impr. {Number(promotion.impressions ?? 0).toLocaleString()}</div>
                    <div>Clics {Number(promotion.clicks ?? 0).toLocaleString()} ({promotion.ctr ?? 0}% CTR)</div>
                    <div>Conv. {Number(promotion.conversions ?? 0)} ({promotion.conversion_rate ?? 0}%)</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {Number(promotion.revenue ?? 0).toLocaleString()}€
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPromotions.length === 0 && (
          <div className="text-center py-12">
            <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune promotion trouvée</h3>
            <p className="text-gray-500">Aucune promotion ne correspond à vos critères de recherche</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromotionInsights;
